import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { fieldWorkApi } from '../api/field-work-api';
import { ConflictError } from '../api/errors';
import { ErrorState } from '../components/ErrorState';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import {
  canTransitionStatus,
  compareQueueOrder,
  fieldWorkBoardColumns,
  priorityLabels,
  statusLabels,
  type FieldWorkRequest,
  type FieldWorkStatus,
} from '../domain/fieldWorkRequest';
import { fieldOpsTeams, teamLabels, type FieldOpsTeam } from '../domain/fieldOpsTeam';
import { useAuth } from '../auth/AuthContext';
import {
  matchesFieldWorkSearch,
  useFieldWorkPlaceNames,
} from '../features/field-work/field-work-search';

function isTeam(value: string | null): value is FieldOpsTeam {
  return fieldOpsTeams.some((team) => team === value);
}

export function FieldWorkBoardPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const teamParam = params.get('team');
  const teamFilter = isTeam(teamParam) ? teamParam : null;
  const query = params.get('q') ?? '';
  const mineOnly = params.get('mine') === 'true';
  const placeNames = useFieldWorkPlaceNames();

  function setParam(name: string, value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value);
    else next.delete(name);
    setParams(next);
  }

  const [dragId, setDragId] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const board = useQuery({
    queryKey: ['field-work'],
    queryFn: () => fieldWorkApi.listFieldWork(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['field-work'] });

  const move = useMutation({
    mutationFn: (input: { id: string; version: number; status: FieldWorkStatus }) =>
      fieldWorkApi.updateFieldWork(input),
    onSuccess: () => {
      setBanner(null);
      void invalidate();
    },
    onError: async (error: unknown) => {
      if (error instanceof ConflictError) {
        setBanner('That card changed since the board loaded — refreshed.');
        await board.refetch();
        return;
      }
      setBanner(error instanceof Error ? error.message : 'The card could not be moved.');
    },
  });
  const reorder = useMutation({
    mutationFn: (input: { team: FieldOpsTeam; ordered: { id: string; version: number }[] }) =>
      fieldWorkApi.reorderFieldWorkQueue(input.team, input.ordered),
    onSuccess: () => {
      setBanner(null);
      void invalidate();
    },
    onError: async (error: unknown) => {
      setBanner(
        error instanceof ConflictError
          ? 'The queue changed since the board loaded — refreshed.'
          : error instanceof Error
            ? error.message
            : 'The queue could not be reordered.',
      );
      await board.refetch();
    },
  });
  const busy = move.isPending || reorder.isPending;

  // Search and "My work" only narrow what's shown; ▲▼ still reorder the
  // card within its team's full queue.
  const scoped = useMemo(
    () =>
      (board.data ?? []).filter(
        (request) =>
          request.status !== 'REJECTED' &&
          (!teamFilter || request.team === teamFilter) &&
          (!mineOnly || request.assignee === (user?.email ?? '')) &&
          matchesFieldWorkSearch(request, query, placeNames),
      ),
    [board.data, teamFilter, mineOnly, user?.email, query, placeNames],
  );

  const columns = useMemo(() => {
    const map = new Map<FieldWorkStatus, FieldWorkRequest[]>(
      fieldWorkBoardColumns.map((status) => [status, []]),
    );
    for (const request of [...scoped].sort(compareQueueOrder)) {
      map.get(request.status)?.push(request);
    }
    return map;
  }, [scoped]);

  // The same-team, same-status neighbours of a card in queue order -- the
  // ▲▼ buttons move a card within that list. Reorder is per-team, so on
  // the "All teams" view it targets the card's own team.
  function teamColumn(request: FieldWorkRequest) {
    return (board.data ?? [])
      .filter(
        (row) =>
          row.team === request.team &&
          row.status === request.status &&
          row.status !== 'REJECTED',
      )
      .sort(compareQueueOrder);
  }

  function canMove(request: FieldWorkRequest, direction: 'up' | 'down') {
    const column = teamColumn(request);
    const at = column.findIndex((row) => row.id === request.id);
    return direction === 'up' ? at > 0 : at >= 0 && at < column.length - 1;
  }

  function moveWithinColumn(request: FieldWorkRequest, direction: 'up' | 'down') {
    if (busy || !canMove(request, direction)) return;
    const column = teamColumn(request);
    const at = column.findIndex((row) => row.id === request.id);
    const swap = column[at + (direction === 'up' ? -1 : 1)];
    if (!swap) return;
    const teamOrder = (board.data ?? [])
      .filter((row) => row.team === request.team && row.status !== 'REJECTED')
      .sort(compareQueueOrder);
    // Rebuild the team's flat queue with `request` moved next to `swap`.
    const without = teamOrder.filter((row) => row.id !== request.id);
    const target = without.findIndex((row) => row.id === swap.id);
    without.splice(direction === 'up' ? target : target + 1, 0, request);
    reorder.mutate({
      team: request.team,
      ordered: without.map((row) => ({ id: row.id, version: row.version })),
    });
  }

  function dropOnColumn(status: FieldWorkStatus) {
    if (!dragId || busy) return;
    const request = (board.data ?? []).find((row) => row.id === dragId);
    setDragId(null);
    if (!request || request.status === status) return;
    if (!canTransitionStatus(request.status, status)) {
      setBanner(`A ${statusLabels[request.status]} card can't move to ${statusLabels[status]}.`);
      return;
    }
    if (status === 'BLOCKED' && !request.blocker?.trim()) {
      setBanner('Add a blocker on the card before moving it to Blocked.');
      return;
    }
    if (status === 'COMPLETE' && !request.completionNotes?.trim()) {
      setBanner('Add completion notes on the card before moving it to Complete.');
      return;
    }
    move.mutate({ id: request.id, version: request.version, status });
  }

  if (board.isLoading) return <PageLoadingState label="Loading board…" />;
  if (board.isError) {
    return (
      <ErrorState
        title="The board could not be loaded"
        rpcName="ListFieldWorkRequests"
        onRetry={() => void board.refetch()}
      />
    );
  }

  return (
    <>
      <PageHeader
        backLink={<Link to="/field-work">Field work</Link>}
        title="Field work board"
        description="Drag a card to another column to change its status. ▲▼ reorders the team queue."
      />

      <div className="region-tabs" role="tablist" aria-label="Field Ops team">
        <button
          type="button"
          role="tab"
          aria-selected={teamFilter === null}
          className={`region-tab${teamFilter === null ? ' is-active' : ''}`}
          onClick={() => setParam('team', '')}
        >
          All teams
        </button>
        {fieldOpsTeams.map((team) => (
          <button
            key={team}
            type="button"
            role="tab"
            aria-selected={teamFilter === team}
            className={`region-tab${teamFilter === team ? ' is-active' : ''}`}
            onClick={() => setParam('team', team)}
          >
            {teamLabels[team]}
          </button>
        ))}
      </div>

      <form
        className="requests-controls"
        role="search"
        aria-label="Filter the board"
        onSubmit={(event) => event.preventDefault()}
      >
        <label className="search-field">
          <span>Search</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setParam('q', event.target.value)}
            placeholder="Title, site, customer or person"
            aria-label="Search the board"
          />
        </label>
        <label className="checkbox-field">
          <span>My work</span>
          <input
            type="checkbox"
            checked={mineOnly}
            onChange={(event) => setParam('mine', event.target.checked ? 'true' : '')}
            aria-label="Show only work assigned to me"
          />
        </label>
      </form>

      {banner ? (
        <p role="alert" className="form-error">
          {banner}
        </p>
      ) : null}

      <div className="kanban-board">
        {fieldWorkBoardColumns.map((status) => {
          const cards = columns.get(status) ?? [];
          return (
            <section
              key={status}
              className="kanban-column"
              aria-label={statusLabels[status]}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => dropOnColumn(status)}
            >
              <h2>
                {statusLabels[status]} <span className="kanban-count">{cards.length}</span>
              </h2>
              {cards.map((request) => (
                <article
                  key={request.id}
                  className="kanban-card"
                  draggable={!busy}
                  onDragStart={() => setDragId(request.id)}
                  onDragEnd={() => setDragId(null)}
                >
                  <Link to={`/field-work/${request.id}`} className="kanban-card-title">
                    {request.title || 'Untitled request'}
                  </Link>
                  <div className="kanban-card-meta">
                    <span>{request.priority ? priorityLabels[request.priority] : 'No priority'}</span>
                    <Link
                      to={`/field-work?assignee=${encodeURIComponent(request.assignee || 'unassigned')}`}
                    >
                      {request.assignee || 'Unassigned'}
                    </Link>
                    {!teamFilter ? (
                      <Link to={`/field-work?team=${request.team}`}>{teamLabels[request.team]}</Link>
                    ) : null}
                  </div>
                  <div className="kanban-card-reorder">
                    <button
                      type="button"
                      aria-label={`Move ${request.title || 'card'} up`}
                      disabled={busy || !canMove(request, 'up')}
                      onClick={() => moveWithinColumn(request, 'up')}
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      aria-label={`Move ${request.title || 'card'} down`}
                      disabled={busy || !canMove(request, 'down')}
                      onClick={() => moveWithinColumn(request, 'down')}
                    >
                      ▼
                    </button>
                    <span className="kanban-card-reorder-hint">
                      {teamFilter ? 'queue order' : `${teamLabels[request.team]} queue`}
                    </span>
                  </div>
                </article>
              ))}
              {cards.length === 0 ? <p className="kanban-empty">—</p> : null}
            </section>
          );
        })}
      </div>
    </>
  );
}
